/**
 * Labstep
 *
 * @module components/OrganizationPlan/Item
 * @desc Displays an organization plan as a card
 */

import { PlanTier, plans } from 'labstep-web/constants/plans';
import { PricingTierCard } from 'labstep-web/core/PricingTier/Card';
import type { OrganizationPlanPrice } from 'labstep-web/models/organization-plan-price.model';
import {
  type Currency,
  CurrencyService,
} from 'labstep-web/services/currency.service';
import type React from 'react';
import { useCallback } from 'react';
import styles from './styles.module.scss';
import type { OrganizationPlanItemProps } from './types';

export const getCustomLabel = (tier: string): React.ReactNode => {
  if (tier === 'academic_free') {
    return 'Free';
  }

  if (tier === 'academic_institute') {
    return (
      <span className={styles.contact}>
        Contact{' '}
        <a href="mailto:sales@labstep.com">sales@labstep.com</a>
      </span>
    );
  }

  return null;
};

export const getPriceMonthly = (
  organizationPlanPrice: OrganizationPlanPrice,
  formatPrice: (unitAmount: number) => string,
): string | null => {
  if (
    organizationPlanPrice.tier === 'academic_free' ||
    !organizationPlanPrice.unit_amount
  ) {
    return null;
  }

  return formatPrice(organizationPlanPrice.unit_amount);
};

export const OrganizationPlanItem: React.FC<
  OrganizationPlanItemProps
> = ({
  organizationPlanPrice,
  onClick,
  isSelected,
  priceCurrency,
  academic,
  priceBeforeDiscount,
}) => {
  const formatPrice = useCallback(
    (unitAmount: number) =>
      CurrencyService.print(
        organizationPlanPrice.interval === 'year'
          ? unitAmount / 12
          : unitAmount,
        priceCurrency.toUpperCase() as Currency,
        false,
      ),
    [priceCurrency, organizationPlanPrice.interval],
  );

  const plan = academic
    ? plans[PlanTier.AcademicPlus]
    : plans[organizationPlanPrice.tier as keyof typeof plans];

  if (!plan) {
    return null;
  }

  return (
    <PricingTierCard
      logoSrc={plan.logoSrc}
      onClick={() => onClick(organizationPlanPrice.id)}
      description={plan.description}
      billingPeriod={organizationPlanPrice.interval}
      priceColor={
        organizationPlanPrice.interval === 'year' ? 'green' : 'blue'
      }
      customLabel={getCustomLabel(organizationPlanPrice.tier)}
      priceMonthly={getPriceMonthly(
        organizationPlanPrice,
        formatPrice,
      )}
      priceBeforeDiscount={
        priceBeforeDiscount
          ? formatPrice(priceBeforeDiscount * 12)
          : undefined
      }
      items={plan.features}
      optionalFeatures={plan.optionalFeatures}
      isSelected={isSelected}
      hidePricing={plan.hidePricing}
    />
  );
};
